import { TicketsReservationByCategory } from "@multicines/services";
import { fetchSessionCorporateTickets } from "@multicines/services";
import { useMoviesStore } from "@multicines/stores";
import { ProductDetailWithCorporate } from "@multicines/types";
import { getProductTotals } from "artisn/products";
import { getShoppingCartProducts, subtractProduct } from "artisn/shopping-cart";
import React, { useState } from "react";

import Styles from "./CorporateTicket.styles";
import { CorporateTicketProps as Props } from "./CorporateTicket.types";
import CodeRedeemInput from "../CodeRedemInput/CodeRedemInput";
import CorporateTicketInfo from "../CorporateTicketInfo/CorporateTicketInfo";
import TicketCardContainer from "components/global/TicketCardContainer/TicketCardContainer";
import { notify } from "config/bugsnag.config";
import CONSTANTS from "config/constants";
import useAnalytics from "hooks/analytics/useAnalytics";
import useI18n from "hooks/useI18n";
import useShoppingCart from "hooks/useShoppingCart/useShoppingCart";
import { useAuthStore } from "stores/auth/auth.store";
import { useStoresStore } from "stores/stores/stores.store";
import { createErrorNotification } from "utils/notifications.utils";
import { createSuccessNotification } from "utils/notifications.utils";

const { ACCOUNT_ID } = CONSTANTS.ARTISN;
const { CHANNEL_ID } = CONSTANTS.ARTISN;

const CorporateTicket: React.FC<Props> = props => {
  const isAnonymous = useAuthStore(store => store.isAnonymous);
  const uid = useAuthStore(store => store.uid);
  const analyticsHook = useAnalytics({
    isSnackWorkflow: false
  });
  const selectedSession = useMoviesStore(store => store.selectedSession);
  const setMustUsePoints = useMoviesStore(store => store.setMustUsePoints);
  const setTicketsCount = useMoviesStore(store => store.setTicketsCount);
  const selectedStore = useStoresStore(store => store.selectedStore);
  const t = useI18n();
  const shoppingCartHook = useShoppingCart();
  const [isLoading, setIsLoading] = useState(false);
  const [tickets, setTickets] = useState<ProductDetailWithCorporate[]>([]);

  const { updateShoppingCart, getShoppingCart, createShoppingCart } =
    shoppingCartHook;
  const { addProduct } = shoppingCartHook;

  const { logSelectTickets, logRemoveTickets } = analyticsHook;

  const [ticketsStore] = selectedStore ?? [];
  const { additionalInfo, storeId } = ticketsStore ?? {};
  const { externalId: externalStoreId } = additionalInfo ?? {};
  const { sessionId } = selectedSession ?? {};

  const handleApplyTicket = async (code: string) => {
    if (typeof isAnonymous === "undefined" || !uid) {
      createErrorNotification(t.common.invalidUser, undefined);
      return;
    }

    if (!sessionId || !storeId || !externalStoreId) {
      createErrorNotification(t.common.invalidSession, undefined);
      return;
    }

    if (!ticketsStore) {
      createErrorNotification(t.errors.storeNotFound, undefined);
      return;
    }

    try {
      setIsLoading(true);

      try {
        const shoppingCart = await getShoppingCart?.();
        if (!shoppingCart) {
          const { id } =
            (await createShoppingCart?.({ channelId: CHANNEL_ID })) ?? {};
          if (!id) {
            throw new Error();
          }
        }
      } catch (error) {
        notify(error, "handleApplyTicket - CorporateTicket");
        createErrorNotification(t.errors.shoppingCart.createCart, undefined);
        setIsLoading(false);
        return;
      }

      const corporateTicket = await fetchSessionCorporateTickets(
        sessionId,
        storeId,
        externalStoreId,
        code
      );
      const { areas } = corporateTicket ?? {};
      const [area] = areas ?? [];
      const { areaCategoryName, products } = area ?? {};
      const [product] = products ?? [];

      const productWithWallet = product as ProductDetailWithCorporate;

      if (!productWithWallet || !areaCategoryName) {
        createErrorNotification(t.wallet.notFound, undefined);
        setIsLoading(false);
        return;
      }

      const alreadyAdded = tickets.find(
        ticket => ticket.voucherBarcode === productWithWallet.voucherBarcode
      );

      if (alreadyAdded) {
        createErrorNotification("", t.wallet.alreadyRedeemed);
        setIsLoading(false);
        return;
      }

      logSelectTickets({ quantity: 1 });
      //INFO: this is logic add ticket to cart, copy from ShoppingCartTicket addHandler
      productWithWallet.attributes.ticketCategory = areaCategoryName;
      const { additionalInfo } = productWithWallet ?? {};
      const { priceInPoints } = additionalInfo ?? {};
      if (priceInPoints) {
        setMustUsePoints(true);
      }
      await addProduct?.(productWithWallet, {
        amount: 1,
        store: ticketsStore,
        anonymous: isAnonymous,
        accountId: ACCOUNT_ID,
        customerId: uid,
        priceCategory: priceInPoints ? "POINTS" : "NORMAL"
      });

      setTickets(prev => {
        return [...prev, productWithWallet];
      });
      setTicketsCount(prev => prev + 1);

      const { voucherBarcode } = productWithWallet;
      const { ticketTypeCode } = additionalInfo ?? {};

      await updateShoppingCart?.(prev => {
        const { additional_info } = prev ?? {};
        const { voucherBarTicketTypes = [] } = additional_info ?? {};

        return {
          ...prev,
          additional_info: {
            ...prev.additional_info,
            voucherBarTicketTypes: [
              ...voucherBarTicketTypes,
              {
                productId: productWithWallet.productId,
                ticketTypeCode,
                quantity: 1,
                barcode: voucherBarcode
              }
            ]
          }
        };
      });
      createSuccessNotification(t.wallet.successRedeem);
    } catch (e) {
      notify(e, "handleApplyTicket - CorporateTicket");
      createErrorNotification("", t.wallet.noRedeem, undefined);
    }

    setIsLoading(false);
  };

  const handleDismissTicket = async (voucherBarcode: string) => {
    if (isLoading) return;

    if (!sessionId || !storeId || !externalStoreId) {
      createErrorNotification(t.common.invalidSession, undefined);
      return;
    }

    if (typeof isAnonymous === "undefined" || !uid) {
      createErrorNotification(t.common.invalidUser, undefined);
      return;
    }

    if (!ticketsStore) {
      createErrorNotification(t.errors.storeNotFound, undefined);
      return;
    }

    try {
      setIsLoading(true);

      const product = tickets.find(
        ticket => ticket.voucherBarcode === voucherBarcode
      );
      if (!product) {
        createErrorNotification(t.wallet.notFound, undefined);
        return;
      }

      const { additionalInfo } = product ?? {};
      const { priceInPoints } = additionalInfo ?? {};
      const newCart = await subtractProduct(product, {
        amount: 1,
        store: ticketsStore,
        anonymous: isAnonymous,
        accountId: ACCOUNT_ID,
        customerId: uid,
        priceCategory: priceInPoints ? "POINTS" : "NORMAL"
      });
      logRemoveTickets({ quantity: 1 });

      setTickets(prev => {
        return prev.filter(ticket => ticket.voucherBarcode !== voucherBarcode);
      });
      setTicketsCount(prev => prev - 1);

      await updateShoppingCart?.(prev => {
        const { additional_info } = prev ?? {};
        const { voucherBarTicketTypes = [] } = additional_info ?? {};

        const voucherBarTicket =
          voucherBarTicketTypes as TicketsReservationByCategory[];

        const remainingVouchers = voucherBarTicket.filter(
          ticket => ticket.barcode !== voucherBarcode
        );

        return {
          ...prev,
          additional_info: {
            ...prev.additional_info,
            voucherBarTicketTypes: remainingVouchers
          }
        };
      });

      createSuccessNotification(t.wallet.successRemove);

      if (!newCart) return;

      const products = getShoppingCartProducts(newCart);
      const productsInCart = products.reduce((acc, next) => {
        return acc + next.amount;
      }, 0);
      if (!productsInCart) {
        setMustUsePoints(false);
      }
    } catch (e) {
      notify(e, "handleDismissTicket - CorporateTicket");
      createErrorNotification(t.wallet.errorRemove, undefined);
    }

    setIsLoading(false);
  };

  return (
    <Styles className="CorporateTicket">
      <CodeRedeemInput onApply={handleApplyTicket} />

      {tickets.length > 0 ? (
        <h3 className="CorporateTicket__title">{t.wallet.corporate}</h3>
      ) : null}

      {tickets.map(ticket => {
        const { name, attributes } = ticket ?? {};
        const { ticketCategory } = attributes ?? {};
        const { voucherBarcode, ticketsQuantity } = ticket ?? {};
        const { netPrice } = getProductTotals(ticket, 1) ?? {};

        if (!voucherBarcode) return null;

        const handleClick = () => {
          handleDismissTicket(voucherBarcode);
        };

        return (
          <TicketCardContainer
            key={voucherBarcode}
            className="CorporateTicket__wrapper"
          >
            <CorporateTicketInfo
              name={`${name} - ${ticketCategory ?? ""}`}
              netPrice={netPrice}
              handleDismissTicket={handleClick}
              ticketsQuantity={ticketsQuantity}
            />
          </TicketCardContainer>
        );
      })}
    </Styles>
  );
};

export default CorporateTicket;
